import { Fragment, useState } from "react"
import { convertNumber, useToggle } from "utils"
import { TableTotalCOA } from "./table-total-coa"
import { BugetManagerMergeListSchema, BugetManagerPayload } from "../types"
import { NumberInput } from "components"
import { CoaHeaderEnum, CoaNameEnum } from "../enum"
import { IoSendSharp } from "react-icons/io5"
import { ModalInputFormula } from "./modal-input-formula"

type TableBodyProps = {
  list: BugetManagerMergeListSchema[]
  item: BugetManagerMergeListSchema
  displayAccountNumber: boolean
  displaySubHeader: boolean
  displayDetail: boolean
  comparisonPNL: string[] 
  budgetInputs?: BugetManagerPayload[]
  setBudgetInputs?: React.Dispatch<React.SetStateAction<BugetManagerPayload[]>>
}

export function TableBody(props: TableBodyProps) {
  const { list, item, displayAccountNumber, displaySubHeader, displayDetail, comparisonPNL, budgetInputs, setBudgetInputs } = props

  const { isActive, toggle } = useToggle(false)

  const [focused, setFocused] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{ coaId: number, defaultValue: number } | null>(null)

  const isNormalFontWeight = [3]?.includes(item?.header_level)
  const isNetAssets = item?.coa_name === CoaNameEnum.NET_ASSETS
  const isNetProfit = item?.coa_name === CoaNameEnum.NET_PROFIT
  const isSubHeader = item?.header_level === CoaHeaderEnum.SUB_HEADER && displaySubHeader
  const isDetail = item?.header_level === CoaHeaderEnum.DETAIL && displayDetail
  const balance = item?.balance[0] ?? null
  const dataBudgetPeriode = Object?.entries(item?.period_balance || {})

  const tdClass = `text-right w-[200px] ${isNormalFontWeight ? "font-normal" : "font-bold"}`

  const handleBudgetChange = (yearmonth: string, value: number) => {
    if (!setBudgetInputs) return;

    const prev = budgetInputs ?? [];
    const existsIndex = prev.findIndex(
      (b) => b?.coa_id === item?.coa_id && b?.year_month === yearmonth
    );

    // Ambil value string dari period_balance → "100000#2"
    const raw = item?.period_balance?.[yearmonth] ?? "0#0";
    const idStr = (raw as string).split("#")[1];
    const budget_manager_id = Number(idStr) !== 0 ? Number(idStr) : undefined;

    let newData: BugetManagerPayload[];
    if (existsIndex >= 0) {
      newData = [...prev];
      newData[existsIndex] = {
        ...newData[existsIndex],
        budget: value,
        ...(budget_manager_id ? { budget_manager_id } : {}),
      };
    } else {
      newData = [
        ...prev,
        {
          coa_id: item?.coa_id,
          year_month: yearmonth,
          budget: value,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        },
      ];
    }

    setBudgetInputs(newData);
  };
  const applyFixedToAllMonths = (value: number) => {
    if (!setBudgetInputs) return;

    const prev = budgetInputs ?? [];
    const yearMonths = Object.keys(item?.period_balance || {});

    let newData = [...prev];

    yearMonths.forEach((yearmonth) => {
      const raw = item?.period_balance?.[yearmonth] ?? "0#0";
      const idStr = (raw as string).split("#")[1];
      const budget_manager_id = Number(idStr) !== 0 ? Number(idStr) : undefined;

      const existsIndex = newData.findIndex(
        (b) => b?.coa_id === item?.coa_id && b?.year_month === yearmonth
      );

      if (existsIndex >= 0) {
        newData[existsIndex] = {
          ...newData[existsIndex],
          budget: value,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        };
      } else {
        newData.push({
          coa_id: item?.coa_id,
          year_month: yearmonth,
          budget: value,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        });
      }
    });

    setBudgetInputs(newData);
  };
  const applyAdjustByAmount = (startValue: number, increment: number) => {
    if (!setBudgetInputs) return;

    const prev = budgetInputs ?? [];
    const yearMonths = Object.keys(item?.period_balance || {}).sort();

    let newData = [...prev];
    let currentValue = startValue;

    yearMonths.forEach((yearmonth, idx) => {
      const raw = item?.period_balance?.[yearmonth] ?? "0#0";
      const idStr = (raw as string).split("#")[1];
      const budget_manager_id = Number(idStr) !== 0 ? Number(idStr) : undefined;

      if (idx === 0) {
        currentValue = startValue;
      } else {
        currentValue = currentValue + increment;
      }

      const existsIndex = newData.findIndex(
        (b) => b?.coa_id === item?.coa_id && b?.year_month === yearmonth
      );

      if (existsIndex >= 0) {
        newData[existsIndex] = {
          ...newData[existsIndex],
          budget: currentValue,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        };
      } else {
        newData.push({
          coa_id: item?.coa_id,
          year_month: yearmonth,
          budget: currentValue,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        });
      }
    });

    setBudgetInputs(newData);
  };
  const applyAdjustByPercentage = (startValue: number, percentage: number) => {
    if (!setBudgetInputs) return;

    const prev = budgetInputs ?? [];
    const yearMonths = Object.keys(item?.period_balance || {}).sort();

    let newData = [...prev];
    let currentValue = startValue;

    yearMonths.forEach((yearmonth, idx) => {
      const raw = item?.period_balance?.[yearmonth] ?? "0#0";
      const idStr = (raw as string).split("#")[1];
      const budget_manager_id = Number(idStr) !== 0 ? Number(idStr) : undefined;

      if (idx === 0) {
        currentValue = startValue;
      } else {
        currentValue = currentValue + currentValue * (percentage / 100);
      }

      const existsIndex = newData.findIndex(
        (b) => b?.coa_id === item?.coa_id && b?.year_month === yearmonth
      );

      if (existsIndex >= 0) {
        newData[existsIndex] = {
          ...newData[existsIndex],
          budget: currentValue,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        };
      } else {
        newData.push({
          coa_id: item?.coa_id,
          year_month: yearmonth,
          budget: currentValue,
          ...(budget_manager_id ? { budget_manager_id } : {}),
        });
      }
    });

    setBudgetInputs(newData);
  };

  return (
    <Fragment>
      <tr
        key={item.coa_id}
        className={balance !== null && balance < 0 ? "text-error" : ""}
        style={{ display: isDetail || isSubHeader || item?.header_level === CoaHeaderEnum.HEADER ? "table-row" : "none" }}
      >
        <td>
          <span
            className={isNormalFontWeight ? "font-normal" : "font-bold"}
            style={{ marginLeft: (item?.coa_level - 1) * 24 }}>

            {item?.coacode && (
              <span className="me-2 text-nowrap">
                {displayAccountNumber ? item?.coacode : ""}
              </span>
            )}

            <span className={displayAccountNumber && item.coacode ? "border-s-[1px] pe-3" : "border-none"} />
            <span className="text-nowrap">{`${item?.coa_name || "-"}`}</span>
          </span>
        </td>

        {isNetAssets || isNetProfit || item?.header_level === CoaHeaderEnum.DETAIL ? item?.balance?.map((balance, index) => (
          <td key={balance + index}  className={tdClass}>
            {convertNumber(balance).intoCurrency}
          </td>
        )) : item?.balance?.map((_) => (<td />))}

        {isNetAssets || isNetProfit || item?.header_level !== CoaHeaderEnum.DETAIL
          ? dataBudgetPeriode?.map((_) => <td />)
          : dataBudgetPeriode?.map(([yearmonth, value], idx) => {
              const firstValue = parseFloat((budgetInputs?.find((b) => b?.coa_id === item?.coa_id && b?.year_month === yearmonth)?.budget ?? value).toString().split("#")[0]) || 0

            return (
              <td key={idx} className="w-[200px]" onMouseEnter={() => idx === 0 && setFocused(true)} onMouseLeave={() => idx === 0 && setFocused(false)}>
                <div className="flex items-center">
                  <NumberInput
                    decimalSeparator="."
                    thousandSeparator=","
                    value={(firstValue).toString()}
                    inputClass="w-[200px] my-[-10px] mx-[-13px] h-[40px] text-right"
                    placeholder="0"
                    onValueChange={(value) => handleBudgetChange(yearmonth, value?.floatValue!)}
                  />
                  {idx === 0 && focused && (
                    <IoSendSharp 
                      className={`ml-5 ${firstValue === 0 ? "cursor-default text-[#8a8a8a]" : "cursor-pointer"}`}
                      onClick={() => {
                        if(firstValue !== 0) {
                          setSelectedRow({ coaId: item.coa_id, defaultValue: firstValue })
                          toggle()
                        }
                      }} 
                    />
                  )}
                </div>
              </td>
            )
            })}
      </tr>

      {list?.map((sub, key) => {
        if (sub?.parent_id === item?.coa_id) {
          return (
            <TableBody
              key={sub.coa_id + key}
              list={list}
              item={sub as BugetManagerMergeListSchema}
              displayAccountNumber={displayAccountNumber}
              displaySubHeader={displaySubHeader}
              displayDetail={displayDetail}
              comparisonPNL={comparisonPNL}
              budgetInputs={budgetInputs}
              setBudgetInputs={setBudgetInputs}

            />
          )
        }
        return <Fragment />
      })}

      <ModalInputFormula 
        show={isActive} 
        onHide={toggle} 
        defaultValue={selectedRow?.defaultValue}
        onApply={(formula, value) => {
          if (formula === "fixed") applyFixedToAllMonths(value)
          if (formula === "amount") {
            const startValue = selectedRow?.defaultValue ?? 0;
            applyAdjustByAmount(startValue, value);
          }
          if (formula === "percentage") {
            const startValue = selectedRow?.defaultValue ?? 0;
            applyAdjustByPercentage(startValue, value);
          }
        }}
      />

      {(isSubHeader || isDetail) && item?.header_level !== CoaHeaderEnum.DETAIL ? <TableTotalCOA item={item} budgetInputs={budgetInputs} list={list} /> : null}
    </Fragment>
  )
}
